import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function Auth() {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const isSignup = pathname === "/signup";
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const googleProcessed = useRef(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    if (!location.hash.includes("session_id=") || googleProcessed.current) return;
    googleProcessed.current = true;
    const sessionId = location.hash.split("session_id=")[1].split("&")[0];
    (async () => {
      try {
        const data = await api.customerGoogleSession(sessionId);
        localStorage.setItem("dr_customer_token", data.token);
        localStorage.setItem("dr_customer_email", data.user.email);
        localStorage.setItem("dr_customer_name", data.user.name || "");
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/account");
      } catch (err) {
        toast.error(formatApiError(err));
        window.history.replaceState(null, "", window.location.pathname);
      }
    })();
  }, [location.hash, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.includes("@")) { toast.error("ENTER A VALID EMAIL"); return; }
    if (form.password.length < 8) { toast.error("PASSWORD MUST BE AT LEAST 8 CHARACTERS"); return; }
    if (isSignup && !form.name.trim()) { toast.error("ENTER YOUR NAME"); return; }
    setBusy(true);
    try {
      const data = isSignup
        ? await api.customerRegister({ name: form.name.trim(), email: form.email, password: form.password })
        : await api.customerLogin({ email: form.email, password: form.password });
      localStorage.setItem("dr_customer_token", data.token);
      localStorage.setItem("dr_customer_email", data.user.email);
      localStorage.setItem("dr_customer_name", data.user.name || "");
      navigate("/account");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full border border-wire bg-transparent px-5 py-4 font-mono text-xs tracking-[0.15em] text-white placeholder:text-neutral-600 focus:border-white focus:outline-none";
  const labelCls = "mb-2 block font-mono text-[10px] tracking-[0.3em] text-neutral-500";

  return (
    <div className="mx-auto max-w-md px-5 pt-36 pb-24 noise-overlay" data-testid={isSignup ? "signup-page" : "login-page"}>
      <p className="font-mono text-[10px] tracking-[0.35em] text-neutral-500">DEJON RICHH — MEMBERS</p>
      <h1 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-white">
        {isSignup ? "Create Account" : "Welcome Back"}
      </h1>
      <p className="mt-4 font-mono text-[11px] leading-relaxed tracking-wider text-neutral-500">
        {isSignup
          ? "ORDER HISTORY, FASTER CHECKOUT, FIRST WORD ON DROPS. STILL NO ACCOUNT NEEDED TO BUY — THIS JUST MAKES IT BETTER."
          : "LOG IN TO SEE YOUR ORDERS AND CHECK OUT FASTER."}
      </p>

      <form onSubmit={submit} className="mt-10 space-y-4" data-testid={isSignup ? "signup-form" : "login-form"}>
        {isSignup && (
          <div>
            <label className={labelCls}>NAME</label>
            <input data-testid="signup-name-input" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="YOUR NAME" />
          </div>
        )}
        <div>
          <label className={labelCls}>EMAIL</label>
          <input data-testid="auth-email-input" type="email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="EMAIL ADDRESS" />
        </div>
        <div>
          <label className={labelCls}>PASSWORD {isSignup && "(MIN 8 CHARACTERS)"}</label>
          <input data-testid="auth-password-input" type="password" value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} placeholder="PASSWORD"
            autoComplete={isSignup ? "new-password" : "current-password"} />
        </div>
        <button data-testid="auth-submit-button" disabled={busy}
          className="group flex w-full items-center justify-center gap-3 bg-white py-4 font-mono text-[11px] tracking-[0.35em] text-black transition-colors hover:bg-neutral-300 disabled:opacity-60">
          {busy ? "ONE MOMENT..." : isSignup ? "CREATE ACCOUNT" : "SIGN IN"}
          <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
        </button>
      </form>

      <div className="mt-6 flex items-center gap-4">
        <span className="h-px flex-1 bg-wire" />
        <span className="font-mono text-[9px] tracking-[0.3em] text-neutral-600">OR</span>
        <span className="h-px flex-1 bg-wire" />
      </div>

      <button data-testid="customer-google-button" onClick={googleLogin}
        className="mt-6 flex w-full items-center justify-center gap-3 border border-wire py-4 font-mono text-[11px] tracking-[0.35em] text-neutral-200 transition-colors hover:border-white hover:text-white">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        CONTINUE WITH GOOGLE
      </button>

      <p className="mt-8 text-center font-mono text-[10px] tracking-[0.25em] text-neutral-500">
        {isSignup ? (
          <>ALREADY HAVE ONE? <Link to="/login" data-testid="goto-login-link" className="text-neutral-200 underline underline-offset-4 hover:text-white">SIGN IN</Link></>
        ) : (
          <>NEW HERE? <Link to="/signup" data-testid="goto-signup-link" className="text-neutral-200 underline underline-offset-4 hover:text-white">CREATE AN ACCOUNT</Link></>
        )}
      </p>
    </div>
  );
}
